/**
 * Supabase案件サービス
 * 案件データの管理を担当
 */

import { SupabaseClient } from '@supabase/supabase-js';
import logger from '../../utils/logger';
import { getSupabaseClient } from '../../utils/supabase';
import { Case, CompanyCase } from '../../types';
import { DataServiceError, ErrorCodes } from '../dataService';
import { isSupabaseAvailable } from '../../utils/environment';
import { DemoDataFallbackService } from '../demoDataFallback';
import { SupabaseServiceFactory } from '../../services/supabase';

export class SupabaseCaseService {
  public supabase: SupabaseClient | null = null;
  public currentCompanyId: string;

  constructor() {
    this.initializeSupabase();
    this.currentCompanyId = 'e32a0499-6456-44ac-9b16-b0001098c259'; // デフォルト会社ID
  }

  private initializeSupabase() {
    try {
      this.supabase = getSupabaseClient();
      if (!this.supabase) {
        logger.warn('Supabaseの設定が見つかりません。環境変数を確認してください。');
        return;
      }
      logger.info('✅ SupabaseCaseService: シングルトンクライアント取得完了');
    } catch (error) {
      logger.error('❌ SupabaseCaseService initialization failed:', error);
    }
  }

  private ensureSupabaseClient() {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.CLIENT_INIT_FAILED
      );
    }
    return this.supabase;
  }

  /**
   * テスト専用メソッド: Supabaseクライアントを設定
   * プロダクションコードでは使用しないこと
   * @param client - 設定するSupabaseクライアント（テスト用）
   */
  public setSupabaseClientForTest(client: SupabaseClient | null): void {
    this.supabase = client;
  }

  // =============================================================================
  // CompanyCase関連メソッド（マルチテナント対応）
  // =============================================================================

  /**
   * 指定した会社の案件を取得
   * @param companyId 会社ID（省略時はcurrentCompanyIdを使用）
   * @returns 案件一覧
   */
  async getCompanyCases(companyId?: string): Promise<CompanyCase[]> {
    const targetCompanyId = companyId || this.currentCompanyId;
    logger.info(`📋 SupabaseCaseService: getCompanyCases開始 - companyId: ${targetCompanyId}`);
    this.ensureSupabaseClient();

    try {
      // FL-191: 全カラム取得に変更（明示的な列挙では多数のフィールドが欠けていた）
      const { data, error } = await this.supabase!.from('company_cases')
        .select('*')
        .eq('company_id', targetCompanyId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new DataServiceError(
          `案件データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const companyCases = (data || []).map((case_: any) => {
        return {
          id: case_.id,
          companyId: case_.company_id,
          caseNumber: case_.case_number || undefined,
          title: case_.title || undefined,
          status: case_.status,
          priority: case_.priority,

          // 取引先情報
          billingPartnerId: case_.billing_partner_id || undefined,
          billingLocationId: case_.billing_location_id || undefined,
          billingAddressCustom: case_.billing_address_custom || undefined,

          pickupPartnerId: case_.pickup_partner_id || undefined,
          pickupLocationId: case_.pickup_location_id || undefined,
          pickupAddressCustom: case_.pickup_address_custom || undefined,
          pickupPhone: case_.pickup_phone || undefined,
          pickupDate: case_.pickup_date,
          pickupDateEnd: case_.pickup_date_end || undefined,
          pickupTimeStart: case_.pickup_time_start || undefined,
          pickupTimeEnd: case_.pickup_time_end || undefined,

          deliveryPartnerId: case_.delivery_partner_id || undefined,
          deliveryLocationId: case_.delivery_location_id || undefined,
          deliveryAddressCustom: case_.delivery_address_custom || undefined,
          deliveryPhone: case_.delivery_phone || undefined,
          deliveryDate: case_.delivery_date,
          deliveryDateEnd: case_.delivery_date_end || undefined,
          deliveryTime: case_.delivery_time || undefined,

          // 車両・陸送情報
          vehiclePlateNumber: case_.vehicle_plate_number || undefined,
          vehiclePlateNumberNone: case_.vehicle_plate_number_none || false,
          vehicleModel: case_.vehicle_model || undefined,
          vehicleModelId: case_.vehicle_model_id || undefined,
          vehicleNumber: case_.vehicle_number ?? undefined, // FL-188: 車体番号（VIN） - null合体演算子使用
          chassisNumber: case_.chassis_number || undefined,
          assignedLandTransporterId: case_.assigned_transporter_id || undefined,
          transportFee: case_.transport_fee || undefined,

          // FL-113: 車検状態管理
          isVehicleInspectionExpired: case_.is_vehicle_inspection_expired || false,
          requiresTemporaryNumber: case_.requires_temporary_number || false,
          temporaryNumberNotes: case_.temporary_number_notes || undefined,

          // 依頼者情報
          requester: case_.requester || undefined,
          managementNumber: case_.management_number || undefined,

          // 進捗情報
          pickupReport: case_.pickup_report || undefined,
          deliveryReport: case_.delivery_report || undefined,

          // その他
          remarks: case_.remarks || undefined,
          notes: case_.notes || undefined,
          price: case_.price || undefined,

          createdAt: case_.created_at,
          updatedAt: case_.updated_at,
          createdBy: case_.created_by || undefined,
        };
      });

      logger.info(`✅ SupabaseCaseService: getCompanyCases完了 - ${companyCases.length}件取得`);
      return companyCases;
    } catch (error) {
      logger.error('❌ SupabaseCaseService: getCompanyCases失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '案件データの取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 指定したIDの案件を取得
   * @param caseId 案件ID
   * @returns 案件データ（見つからない場合はnull）
   */
  async getCaseById(caseId: string): Promise<CompanyCase | null> {
    logger.info(`📋 SupabaseCaseService: getCaseById開始 - caseId: ${caseId}`);
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_cases')
        .select('*')
        .eq('id', caseId)
        .eq('company_id', this.currentCompanyId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // 案件が見つからない場合
          logger.warn(`⚠️ SupabaseCaseService: 案件が見つかりません - caseId: ${caseId}`);
          return null;
        }
        throw new DataServiceError(
          `案件データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      if (!data) {
        logger.warn(`⚠️ SupabaseCaseService: 案件データが空です - caseId: ${caseId}`);
        return null;
      }

      logger.info(`[TRANSPORTER-DEBUG] 📖 DB生データ取得`, {
        caseId,
        rawTransporterId: data.transporter_id,
        rawType: typeof data.transporter_id,
        isNull: data.transporter_id === null,
        isUndefined: data.transporter_id === undefined,
        isEmpty: data.transporter_id === '',
      });

      const companyCase: CompanyCase = {
        id: data.id,
        companyId: data.company_id,
        caseNumber: data.case_number || undefined,
        title: data.title || undefined,
        status: data.status,
        priority: data.priority,

        // 取引先情報
        billingPartnerId: data.billing_partner_id || undefined,
        billingLocationId: data.billing_location_id || undefined,
        billingAddressCustom: data.billing_address_custom || undefined,

        pickupPartnerId: data.pickup_partner_id || undefined,
        pickupLocationId: data.pickup_location_id || undefined,
        pickupAddressCustom: data.pickup_address_custom || undefined,
        pickupPhone: data.pickup_phone || undefined,
        pickupDate: data.pickup_date,
        pickupDateEnd: data.pickup_date_end || undefined,
        pickupTimeStart: data.pickup_time_start || undefined,
        pickupTimeEnd: data.pickup_time_end || undefined,

        deliveryPartnerId: data.delivery_partner_id || undefined,
        deliveryLocationId: data.delivery_location_id || undefined,
        deliveryAddressCustom: data.delivery_address_custom || undefined,
        deliveryPhone: data.delivery_phone || undefined,
        deliveryDate: data.delivery_date,
        deliveryDateEnd: data.delivery_date_end || undefined,
        deliveryTime: data.delivery_time || undefined,
        deliveryTimeStart: data.delivery_time_start || undefined,
        deliveryTimeEnd: data.delivery_time_end || undefined,

        // 車両・陸送情報
        vehiclePlateNumber: data.vehicle_plate_number || undefined,
        vehiclePlateNumberNone: data.vehicle_plate_number_none || false,
        vehicleModel: data.vehicle_model || undefined,
        vehicleModelId: data.vehicle_model_id || undefined,
        vehicleNumber: data.vehicle_number || undefined,
        chassisNumber: data.chassis_number || undefined,
        assignedLandTransporterId: data.assigned_transporter_id || undefined,
        transportFee: data.transport_fee || undefined,

        // 依頼者情報
        requester: data.requester || undefined,
        managementNumber: data.management_number || undefined,

        // 進捗情報
        pickupReport: data.pickup_report || undefined,
        deliveryReport: data.delivery_report || undefined,

        // FL-113: 車検状態管理
        isVehicleInspectionExpired: data.is_vehicle_inspection_expired || false,
        requiresTemporaryNumber: data.requires_temporary_number || false,
        temporaryNumberNotes: data.temporary_number_notes || undefined,

        // その他
        remarks: data.remarks || undefined,
        notes: data.notes || undefined,
        price: data.price || undefined,

        createdAt: data.created_at,
        updatedAt: data.updated_at,
        createdBy: data.created_by || undefined,
      };

      logger.info(`[TRANSPORTER-DEBUG] 🔄 変換後データ`, {
        caseId,
        rawTransporterId: data.transporter_id,
        convertedAssignedLandTransporterId: companyCase.assignedLandTransporterId,
        convertedType: typeof companyCase.assignedLandTransporterId,
        isNull: companyCase.assignedLandTransporterId === null,
        isUndefined: companyCase.assignedLandTransporterId === undefined,
      });

      logger.info(`[TRANSPORTER-DEBUG] ✅ getCaseById完了`, {
        caseId,
        finalAssignedLandTransporterId: companyCase.assignedLandTransporterId,
        finalType: typeof companyCase.assignedLandTransporterId,
        isNull: companyCase.assignedLandTransporterId === null,
        isUndefined: companyCase.assignedLandTransporterId === undefined,
      });

      return companyCase;
    } catch (error) {
      logger.error('❌ SupabaseCaseService: getCaseById失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '案件データの取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 新しい会社案件を作成
   * @param caseData 案件データ（IDなし）
   * @returns 作成された案件データ
   */
  async createCompanyCase(caseData: Omit<CompanyCase, 'id'>): Promise<CompanyCase> {
    logger.info('🚀 SupabaseCaseService: createCompanyCase開始');
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_cases')
        .insert([
          {
            company_id: caseData.companyId,
            case_number: caseData.caseNumber,
            title: caseData.title,
            status: caseData.status,
            priority: caseData.priority,
            billing_partner_id: caseData.billingPartnerId,
            billing_location_id: caseData.billingLocationId,
            billing_address_custom: caseData.billingAddressCustom,
            pickup_partner_id: caseData.pickupPartnerId,
            pickup_location_id: caseData.pickupLocationId,
            pickup_address_custom: caseData.pickupAddressCustom,
            pickup_phone: caseData.pickupPhone,
            pickup_date: caseData.pickupDate,
            pickup_date_end: caseData.pickupDateEnd,
            pickup_time_start: caseData.pickupTimeStart,
            pickup_time_end: caseData.pickupTimeEnd,
            delivery_partner_id: caseData.deliveryPartnerId,
            delivery_location_id: caseData.deliveryLocationId,
            delivery_address_custom: caseData.deliveryAddressCustom,
            delivery_phone: caseData.deliveryPhone,
            delivery_date: caseData.deliveryDate,
            delivery_date_end: caseData.deliveryDateEnd,
            delivery_time: caseData.deliveryTime,
            vehicle_plate_number: caseData.vehiclePlateNumber,
            vehicle_plate_number_none: caseData.vehiclePlateNumberNone,
            vehicle_model: caseData.vehicleModel,
            assigned_transporter_id: caseData.assignedLandTransporterId,
            transport_fee: caseData.transportFee,
            requester: caseData.requester,
            management_number: caseData.managementNumber,
            pickup_report: caseData.pickupReport,
            delivery_report: caseData.deliveryReport,
            remarks: caseData.remarks,
            notes: caseData.notes,
            price: caseData.price,
            vehicle_model_id: caseData.vehicleModelId,
            is_vehicle_inspection_expired: caseData.isVehicleInspectionExpired,
            requires_temporary_number: caseData.requiresTemporaryNumber,
            temporary_number_notes: caseData.temporaryNumberNotes,
            created_by: caseData.createdBy,
            // FL-163: 車体番号 (acceptanceStatus removed - now using status)
            chassis_number: caseData.chassisNumber,
          },
        ])
        .select();

      if (error) {
        throw new DataServiceError(
          `案件の作成中にエラーが発生しました: ${error.message}`,
          ErrorCodes.DATA_CREATION_FAILED,
          error
        );
      }

      // company_casesテーブルから作成された案件を取得（vehicle_display_nameを含む）
      const { data: createdData, error: fetchError } = await this.supabase!.from('company_cases')
        .select('*')
        .eq('id', data[0].id)
        .single();

      if (fetchError || !createdData) {
        throw new DataServiceError(
          `作成された案件の取得に失敗しました（ID: ${data[0].id}）: ${fetchError?.message || '不明なエラー'}`,
          ErrorCodes.DATA_FETCH_FAILED,
          fetchError || undefined
        );
      }

      // データベーススキーマからCompanyCase型への変換
      const createdCase: CompanyCase = {
        id: createdData.id,
        companyId: createdData.company_id,
        caseNumber: createdData.case_number,
        title: createdData.title,
        status: createdData.status,
        priority: createdData.priority,
        billingPartnerId: createdData.billing_partner_id,
        billingLocationId: createdData.billing_location_id,
        billingAddressCustom: createdData.billing_address_custom,
        pickupPartnerId: createdData.pickup_partner_id,
        pickupLocationId: createdData.pickup_location_id,
        pickupAddressCustom: createdData.pickup_address_custom,
        pickupPhone: createdData.pickup_phone,
        pickupDate: createdData.pickup_date,
        pickupDateEnd: createdData.pickup_date_end,
        pickupTimeStart: createdData.pickup_time_start,
        pickupTimeEnd: createdData.pickup_time_end,
        deliveryPartnerId: createdData.delivery_partner_id,
        deliveryLocationId: createdData.delivery_location_id,
        deliveryAddressCustom: createdData.delivery_address_custom,
        deliveryPhone: createdData.delivery_phone,
        deliveryDate: createdData.delivery_date,
        deliveryDateEnd: createdData.delivery_date_end,
        deliveryTime: createdData.delivery_time,
        vehiclePlateNumber: createdData.vehicle_plate_number,
        vehiclePlateNumberNone: createdData.vehicle_plate_number_none,
        vehicleModel: createdData.vehicle_model,
        vehicleDisplayName: createdData.vehicle_display_name,
        assignedLandTransporterId: createdData.assigned_transporter_id,
        transportFee: createdData.transport_fee,
        requester: createdData.requester,
        managementNumber: createdData.management_number,
        pickupReport: createdData.pickup_report,
        deliveryReport: createdData.delivery_report,
        remarks: createdData.remarks,
        notes: createdData.notes,
        price: createdData.price,
        vehicleModelId: createdData.vehicle_model_id,
        isVehicleInspectionExpired: createdData.is_vehicle_inspection_expired,
        requiresTemporaryNumber: createdData.requires_temporary_number,
        temporaryNumberNotes: createdData.temporary_number_notes,
        createdAt: createdData.created_at,
        updatedAt: createdData.updated_at,
        createdBy: createdData.created_by,
        // FL-163: 車体番号 (acceptanceStatus removed - now using status)
        chassisNumber: createdData.chassis_number || undefined,
      };

      logger.info(`✅ SupabaseCaseService: createCompanyCase完了 - ${createdCase.id}`);
      return createdCase;
    } catch (error) {
      logger.error('❌ SupabaseCaseService: createCompanyCase失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        '案件の作成中にエラーが発生しました',
        ErrorCodes.DATA_CREATION_FAILED,
        error as Error
      );
    }
  }

  // =============================================================================
  // Case関連メソッド（レガシー互換性維持）
  // =============================================================================

  /**
   * 案件一覧を取得（レガシー）
   * @returns 案件一覧
   */
  async getCases(): Promise<Case[]> {
    logger.info('📋 SupabaseCaseService: getCases開始（レガシー）');

    // Supabase未設定時のみフォールバックデータを返す（デモモードでもリモート接続）
    if (!isSupabaseAvailable() || !this.supabase) {
      logger.info('ℹ️ SupabaseCaseService: Supabase未設定のため、フォールバックデータを返します');
      return DemoDataFallbackService.getCases();
    }

    const supabase = this.ensureSupabaseClient();

    try {
      const companyId = this.currentCompanyId;
      logger.info(`🔍 SupabaseCaseService: getCases実行 - companyId: ${companyId}`);

      const { data, error } = await supabase
        .from('company_cases')
        .select('*')
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) {
        logger.error('❌ SupabaseCaseService: getCasesクエリエラー:', error);
        throw new DataServiceError(
          `案件データの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_FETCH_FAILED,
          error
        );
      }

      // データベーススキーマからTypeScript型への変換
      const cases: Case[] = data.map(
        (row: any): Case => ({
          id: row.id,
          status: row.status as Case['status'], // FL-142: 新規8状態対応
          billingPartnerId: row.billing_partner_id || undefined,
          pickupPartnerId: row.pickup_partner_id || undefined,
          pickupAddress: row.pickup_address_custom || undefined,
          pickupPhone: row.pickup_phone || undefined,
          pickupDate: row.pickup_date,
          pickupTimeStart: row.pickup_time_start || undefined,
          pickupTimeEnd: row.pickup_time_end || undefined,
          deliveryPartnerId: row.delivery_partner_id || undefined,
          deliveryAddress: row.delivery_address_custom || undefined,
          deliveryPhone: row.delivery_phone || undefined,
          deliveryDate: row.delivery_date,
          deliveryTime: row.delivery_time || undefined,
          vehiclePlateNumber: row.vehicle_plate_number || undefined,
          vehicleModel: row.vehicle_model || undefined,
          vehicleModelId: row.vehicle_model_id || undefined,
          vehicleDisplayName: row.vehicle_display_name || undefined,
          vehicleType: row.vehicle_type || '',
          assignedLandTransporterId: row.assigned_transporter_id || row.transporter_id || undefined,
          transportFee: row.transport_fee || undefined,
          price: row.price || undefined,
          remarks: row.remarks || undefined,
          // Legacy Client IDsの追加
          billingClientId: row.billing_client_id || '',
          pickupClientId: row.pickup_client_id || '',
          deliveryClientId: row.delivery_client_id || '',
          // レポートの追加
          pickupReport: row.pickup_report || null,
          deliveryReport: row.delivery_report || null,

          // FL-163: 車体番号管理 (acceptanceStatus removed - now using status)
          chassisNumber: row.chassis_number || undefined,
        })
      );

      logger.info('✅ SupabaseCaseService: 案件データ取得成功', {
        count: cases.length,
        companyId,
        statuses: cases.map(c => c.status).join(', '),
      });

      return cases;
    } catch (error) {
      logger.error('❌ SupabaseCaseService: getCases実行エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError('案件データの取得に失敗しました', ErrorCodes.UNKNOWN_ERROR);
    }
  }

  /**
   * 新しい案件を作成（レガシー）
   * @param case_ 案件データ（IDなし）
   * @returns 作成された案件データ
   */
  async createCase(case_: Omit<Case, 'id'>): Promise<Case> {
    logger.info('🚀 SupabaseCaseService: createCase開始（レガシー）');
    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('company_cases')
        .insert({
          company_id: this.currentCompanyId,
          status: case_.status || 'unassigned',
          transporter_id: case_.assignedLandTransporterId,
          billing_partner_id: case_.billingPartnerId,
          pickup_partner_id: case_.pickupPartnerId,
          delivery_partner_id: case_.deliveryPartnerId,
          // FL-89: 自由入力モード対応 - 住所をcustomフィールドに格納
          pickup_address_custom: case_.pickupAddressCustom,
          pickup_phone: case_.pickupPhone,
          pickup_date: case_.pickupDate,
          pickup_time_start: case_.pickupTimeStart,
          pickup_time_end: case_.pickupTimeEnd,
          delivery_address_custom: case_.deliveryAddressCustom,
          delivery_phone: case_.deliveryPhone,
          delivery_date: case_.deliveryDate,
          delivery_time: case_.deliveryTime,
          vehicle_model: case_.vehicleModel,
          vehicle_plate_number: case_.vehiclePlateNumber,
          // FL-163: 車体番号 (acceptanceStatus removed - now using status)
          chassis_number: case_.chassisNumber,
          price: case_.price || 0,
          remarks: case_.remarks,
        })
        .select()
        .single();

      if (error) {
        logger.error('❌ SupabaseCaseService: createCase作成エラー:', error);
        throw new DataServiceError(
          `案件の作成に失敗しました: ${error.message}`,
          ErrorCodes.DATA_CREATION_FAILED,
          error
        );
      }

      logger.info('✅ 案件作成完了:', {
        id: data.id,
        vehicleModel: data.vehicle_model,
        vehiclePlateNumber: data.vehicle_plate_number,
      });

      // データベースの形式からCase型に変換
      return this.convertToCaseFormat(data);
    } catch (error) {
      logger.error('❌ 案件作成エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError('案件の作成に失敗しました', ErrorCodes.DATA_CREATION_FAILED);
    }
  }

  /**
   * 案件を更新（レガシー）
   * @param id 案件ID
   * @param case_ 更新データ
   * @returns 更新された案件データ
   */
  async updateCase(id: string, case_: Partial<Case>): Promise<Case> {
    logger.info(`🔧 SupabaseCaseService: updateCase開始 - ID: ${id}`);
    logger.info(`[TRANSPORTER-DEBUG] 🔧 updateCase開始`, {
      caseId: id,
      inputAssignedLandTransporterId: case_.assignedLandTransporterId,
      inputType: typeof case_.assignedLandTransporterId,
      hasProperty: 'assignedLandTransporterId' in case_,
    });
    logger.debug('📥 updateCase入力パラメータ:', { case_ });
    this.ensureSupabaseClient();

    try {
      const updateData: any = {};

      // 更新するフィールドのみをセット
      if (case_.status !== undefined) updateData.status = case_.status;
      if (case_.assignedLandTransporterId !== undefined) {
        logger.info(`[TRANSPORTER-DEBUG] ✅ 陸送員更新ON`, {
          value: case_.assignedLandTransporterId,
          type: typeof case_.assignedLandTransporterId,
          isNull: case_.assignedLandTransporterId === null,
          isEmpty: case_.assignedLandTransporterId === '',
          willSetTo_transporter_id: case_.assignedLandTransporterId,
        });
        updateData.transporter_id = case_.assignedLandTransporterId;
      } else {
        logger.warn(`[TRANSPORTER-DEBUG] ⚠️ 陸送員更新SKIP`, {
          reason: 'assignedLandTransporterId is undefined',
          hasProperty: 'assignedLandTransporterId' in case_,
        });
      } // null許容: nullは「未割り当て」を意味し、DBでも許可されている
      if (case_.billingPartnerId !== undefined)
        updateData.billing_partner_id = case_.billingPartnerId;
      if (case_.pickupPartnerId !== undefined) updateData.pickup_partner_id = case_.pickupPartnerId;
      if (case_.deliveryPartnerId !== undefined)
        updateData.delivery_partner_id = case_.deliveryPartnerId;
      if (case_.pickupAddress !== undefined) updateData.pickup_address_custom = case_.pickupAddress;
      if (case_.pickupPhone !== undefined) updateData.pickup_phone = case_.pickupPhone;
      if (case_.pickupDate !== undefined) updateData.pickup_date = case_.pickupDate;
      if (case_.pickupTimeStart !== undefined) updateData.pickup_time_start = case_.pickupTimeStart;
      if (case_.pickupTimeEnd !== undefined) updateData.pickup_time_end = case_.pickupTimeEnd;
      if (case_.deliveryAddress !== undefined)
        updateData.delivery_address_custom = case_.deliveryAddress;
      if (case_.deliveryPhone !== undefined) updateData.delivery_phone = case_.deliveryPhone;
      if (case_.deliveryDate !== undefined) updateData.delivery_date = case_.deliveryDate;
      if (case_.deliveryTime !== undefined) updateData.delivery_time = case_.deliveryTime;
      if (case_.vehiclePlateNumber !== undefined)
        updateData.vehicle_plate_number = case_.vehiclePlateNumber;
      if (case_.vehicleModel !== undefined) updateData.vehicle_model = case_.vehicleModel;
      if (case_.transportFee !== undefined) updateData.transport_fee = case_.transportFee;
      if (case_.price !== undefined) updateData.price = case_.price;
      if (case_.remarks !== undefined) updateData.remarks = case_.remarks;
      // FL-163: 車体番号 (acceptanceStatus removed - now using status)
      if (case_.chassisNumber !== undefined) updateData.chassis_number = case_.chassisNumber;

      // 更新日時を自動設定
      updateData.updated_at = new Date().toISOString();

      logger.info(`[TRANSPORTER-DEBUG] 📤 Supabase送信データ`, {
        caseId: id,
        updateDataKeys: Object.keys(updateData),
        hasTransporterId: 'transporter_id' in updateData,
        transporter_id: updateData.transporter_id,
      });
      logger.debug('📤 Supabase送信データ詳細:', { updateData });
      logger.info(
        `🚀 Supabase更新実行: ID=${id}, 更新フィールド数=${Object.keys(updateData).length}`
      );

      const { data, error } = await this.supabase!.from('company_cases')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        logger.error('❌ SupabaseCaseService: updateCase更新エラー:', error);
        throw new DataServiceError(
          `案件の更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      logger.info(`[TRANSPORTER-DEBUG] 📥 DB更新後データ`, {
        caseId: id,
        dbTransporterId: data.transporter_id,
        dbType: typeof data.transporter_id,
        isNull: data.transporter_id === null,
        isUndefined: data.transporter_id === undefined,
      });
      logger.debug('📥 DB返却データ:', { data });
      logger.info('✅ 案件更新完了:', id);

      // データベースの形式からCase型に変換
      return this.convertToCaseFormat(data);
    } catch (error) {
      logger.error('❌ 案件更新エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError('案件の更新に失敗しました', ErrorCodes.DATA_UPDATE_FAILED);
    }
  }

  /**
   * 案件を削除（レガシー）
   * @param id 案件ID
   */
  async deleteCase(id: string): Promise<void> {
    logger.info(`🗑️ SupabaseCaseService: deleteCase開始 - ID: ${id}`);
    this.ensureSupabaseClient();

    try {
      const { error } = await this.supabase!.from('company_cases').delete().eq('id', id);

      if (error) {
        logger.error('❌ 案件削除エラー:', error);
        throw new DataServiceError(
          `案件の削除に失敗しました: ${error.message}`,
          ErrorCodes.DATA_DELETION_FAILED,
          error
        );
      }

      logger.info('✅ 案件削除完了:', id);
    } catch (error) {
      logger.error('❌ 案件削除エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError('案件の削除に失敗しました', ErrorCodes.DATA_DELETION_FAILED);
    }
  }

  // =============================================================================
  // 車両番号重複チェック機能
  // =============================================================================

  /**
   * 車両番号の重複をチェック
   * @param plateNumber チェックする車両番号
   * @param companyId 会社ID
   * @param excludeCaseId 除外する案件ID（更新時に自身を除外）
   * @returns 重複チェック結果
   */
  async checkVehiclePlateNumberDuplicate(
    plateNumber: string,
    companyId: string,
    excludeCaseId?: string
  ): Promise<{
    isDuplicate: boolean;
    duplicateCases: Array<{
      id: string;
      caseNumber: string;
      vehiclePlateNumber: string;
      pickupDate: string;
      status: string;
    }>;
  }> {
    logger.info('🔍 SupabaseCaseService: checkVehiclePlateNumberDuplicate開始', {
      plateNumber,
      companyId,
      excludeCaseId,
    });

    // 空の車両番号の場合は重複なしとして処理
    if (!plateNumber || plateNumber.trim() === '') {
      return {
        isDuplicate: false,
        duplicateCases: [],
      };
    }

    this.ensureSupabaseClient();

    try {
      let query = this.supabase!.from('company_cases')
        .select('id, case_number, vehicle_plate_number, pickup_date, status')
        .eq('company_id', companyId)
        .eq('vehicle_plate_number', plateNumber.trim());

      // 更新時は自身のIDを除外
      if (excludeCaseId) {
        query = query.neq('id', excludeCaseId);
      }

      const { data, error } = await query.order('pickup_date', { ascending: false });

      if (error) {
        logger.error('❌ 車両番号重複チェックエラー:', error);
        throw new DataServiceError(
          `車両番号重複チェックに失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const duplicateCases = (data || []).map((row: any) => ({
        id: row.id,
        caseNumber: row.case_number || '',
        vehiclePlateNumber: row.vehicle_plate_number,
        pickupDate: row.pickup_date,
        status: row.status,
      }));

      const result = {
        isDuplicate: duplicateCases.length > 0,
        duplicateCases,
      };

      logger.info('✅ 車両番号重複チェック完了:', result);
      return result;
    } catch (error) {
      logger.error('❌ 車両番号重複チェック実行エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError(
            '車両番号重複チェックに失敗しました',
            ErrorCodes.DATA_ACCESS_ERROR,
            error as Error
          );
    }
  }

  // =============================================================================
  // ステータス管理メソッド（FL-142: 案件ステータス機能追加）
  // =============================================================================

  /**
   * 案件のステータスを更新し、履歴を記録
   * @param caseId 案件ID
   * @param newStatus 新しいステータス
   * @param notes ステータス変更の理由・メモ
   * @param changedBy 変更者ユーザーID
   * @returns 更新後の案件データ
   */
  async updateCaseStatus(
    caseId: string,
    newStatus: string,
    notes?: string,
    changedBy?: string
  ): Promise<CompanyCase> {
    logger.info(`🔄 SupabaseCaseService: updateCaseStatus開始`, {
      caseId,
      newStatus,
      changedBy,
    });

    this.ensureSupabaseClient();

    try {
      // 1. 現在のステータスを取得
      const currentCase = await this.getCaseById(caseId);
      if (!currentCase) {
        throw new DataServiceError(
          `案件が見つかりません（ID: ${caseId}）`,
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      const previousStatus = currentCase.status;

      // 2. ステータスを更新
      const { data, error } = await this.supabase!.from('company_cases')
        .update({
          status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', caseId)
        .select()
        .single();

      if (error) {
        throw new DataServiceError(
          `ステータス更新に失敗しました: ${error.message}`,
          ErrorCodes.DATA_UPDATE_FAILED,
          error
        );
      }

      // 3. ステータス履歴を記録
      logger.info(`🔄 ステータス履歴の記録を開始`, {
        caseId,
        previousStatus,
        newStatus,
        changedBy,
        hasNotes: !!notes,
      });

      const historyPayload = {
        case_id: caseId,
        previous_status: previousStatus,
        new_status: newStatus,
        changed_by: changedBy || null,
        notes: notes || null,
      };

      const { data: insertedHistory, error: historyError } = await this.supabase!.from(
        'case_status_history'
      )
        .insert(historyPayload)
        .select()
        .single();

      if (historyError) {
        logger.error(`❌ ステータス履歴の記録に失敗`, {
          caseId,
          previousStatus,
          newStatus,
          changedBy,
          errorCode: historyError.code,
          errorMessage: historyError.message,
          errorDetails: historyError.details,
          errorHint: historyError.hint,
          insertPayload: historyPayload,
        });
        // 履歴記録失敗はログするが、ステータス更新は成功したため継続
        // （監査ログの蓄積は重要だが、ユーザー作業をブロックしない設計）
      } else if (insertedHistory) {
        logger.info(`✅ ステータス履歴を記録成功`, {
          historyId: insertedHistory.id,
          caseId,
          previousStatus,
          newStatus,
          recordedAt: insertedHistory.created_at,
        });
      } else {
        logger.warn(`⚠️ ステータス履歴のINSERT結果が返されない`, {
          caseId,
          previousStatus,
          newStatus,
          insertPayload: historyPayload,
        });
      }

      // 4. 更新後の案件データを返す
      const updatedCase: CompanyCase = {
        id: data.id,
        companyId: data.company_id,
        caseNumber: data.case_number || undefined,
        title: data.title || undefined,
        status: data.status,
        priority: data.priority,
        billingPartnerId: data.billing_partner_id || undefined,
        billingLocationId: data.billing_location_id || undefined,
        billingAddressCustom: data.billing_address_custom || undefined,
        pickupPartnerId: data.pickup_partner_id || undefined,
        pickupLocationId: data.pickup_location_id || undefined,
        pickupAddressCustom: data.pickup_address_custom || undefined,
        pickupPhone: data.pickup_phone || undefined,
        pickupDate: data.pickup_date,
        pickupDateEnd: data.pickup_date_end || undefined,
        pickupTimeStart: data.pickup_time_start || undefined,
        pickupTimeEnd: data.pickup_time_end || undefined,
        deliveryPartnerId: data.delivery_partner_id || undefined,
        deliveryLocationId: data.delivery_location_id || undefined,
        deliveryAddressCustom: data.delivery_address_custom || undefined,
        deliveryPhone: data.delivery_phone || undefined,
        deliveryDate: data.delivery_date,
        deliveryDateEnd: data.delivery_date_end || undefined,
        deliveryTime: data.delivery_time || undefined,
        vehiclePlateNumber: data.vehicle_plate_number || undefined,
        vehiclePlateNumberNone: data.vehicle_plate_number_none || false,
        vehicleModel: data.vehicle_model || undefined,
        vehicleModelId: data.vehicle_model_id || undefined,
        vehicleNumber: data.vehicle_number || undefined,
        chassisNumber: data.chassis_number || undefined,
        assignedLandTransporterId: data.assigned_transporter_id || undefined,
        transportFee: data.transport_fee || undefined,
        isVehicleInspectionExpired: data.is_vehicle_inspection_expired || false,
        requiresTemporaryNumber: data.requires_temporary_number || false,
        temporaryNumberNotes: data.temporary_number_notes || undefined,
        requester: data.requester || undefined,
        managementNumber: data.management_number || undefined,
        pickupReport: data.pickup_report || undefined,
        deliveryReport: data.delivery_report || undefined,
        remarks: data.remarks || undefined,
        notes: data.notes || undefined,
        price: data.price || undefined,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        createdBy: data.created_by || undefined,
      };

      logger.info(`✅ SupabaseCaseService: updateCaseStatus完了`, {
        caseId,
        previousStatus,
        newStatus,
      });

      return updatedCase;
    } catch (error) {
      logger.error('❌ SupabaseCaseService: updateCaseStatus失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'ステータス更新に失敗しました',
        ErrorCodes.DATA_UPDATE_FAILED,
        error as Error
      );
    }
  }

  /**
   * 案件のステータス変更履歴を取得
   * @param caseId 案件ID
   * @returns ステータス変更履歴の一覧
   */
  async getCaseStatusHistory(caseId: string): Promise<
    Array<{
      id: string;
      caseId: string;
      previousStatus: string | null;
      newStatus: string;
      changedBy: string | null;
      notes: string | null;
      createdAt: string;
    }>
  > {
    logger.info(`📋 SupabaseCaseService: getCaseStatusHistory開始`, { caseId });

    this.ensureSupabaseClient();

    try {
      const { data, error } = await this.supabase!.from('case_status_history')
        .select('*')
        .eq('case_id', caseId)
        .order('created_at', { ascending: false });

      if (error) {
        throw new DataServiceError(
          `ステータス履歴の取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR,
          error
        );
      }

      const history = (data || []).map((row: any) => ({
        id: row.id,
        caseId: row.case_id,
        previousStatus: row.previous_status,
        newStatus: row.new_status,
        changedBy: row.changed_by,
        notes: row.notes,
        createdAt: row.created_at,
      }));

      logger.info(`✅ SupabaseCaseService: getCaseStatusHistory完了`, {
        caseId,
        count: history.length,
      });

      return history;
    } catch (error) {
      logger.error('❌ SupabaseCaseService: getCaseStatusHistory失敗:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'ステータス履歴の取得に失敗しました',
        ErrorCodes.DATA_ACCESS_ERROR,
        error as Error
      );
    }
  }

  /**
   * 現在のドライバー（陸送員）に割り当てられた案件を取得
   * 陸送員ページ用のメソッド
   * @returns ドライバーに割り当てられた案件一覧
   */
  async getDriverAssignedCases(): Promise<Case[]> {
    logger.info('📋 SupabaseCaseService: getDriverAssignedCases開始');
    this.ensureSupabaseClient();

    try {
      // 1. 現在のドライバーを取得
      const authService = SupabaseServiceFactory.getAuthService();
      const currentUser = await authService.getCurrentUser();

      if (!currentUser) {
        throw new DataServiceError(
          'ユーザーが認証されていません',
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      const driverId = currentUser.landTransporterId;
      if (!driverId) {
        logger.warn('⚠️ SupabaseCaseService: ドライバーIDが見つかりません');
        return [];
      }

      logger.info(`🔍 SupabaseCaseService: ドライバーのケース取得実行 - driverId: ${driverId}`);

      // 2. ドライバーに割り当てられた案件を取得
      const { data, error } = await this.supabase!.from('company_cases')
        .select('*')
        .eq('assigned_transporter_id', driverId)
        .order('pickup_date', { ascending: true });

      if (error) {
        logger.error('❌ SupabaseCaseService: getDriverAssignedCasesクエリエラー:', error);
        throw new DataServiceError(
          `ドライバー割り当て案件の取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_FETCH_FAILED,
          error
        );
      }

      // 3. データベーススキーマからCase型への変換
      const cases: Case[] = (data || []).map(
        (row: any): Case => ({
          id: row.id,
          status: row.status as Case['status'],
          billingPartnerId: row.billing_partner_id || undefined,
          pickupPartnerId: row.pickup_partner_id || undefined,
          pickupAddress: row.pickup_address_custom || row.pickup_address || '',
          pickupPhone: row.pickup_phone || undefined,
          pickupDate: row.pickup_date,
          pickupTimeStart: row.pickup_time_start || undefined,
          pickupTimeEnd: row.pickup_time_end || undefined,
          deliveryPartnerId: row.delivery_partner_id || undefined,
          deliveryAddress: row.delivery_address_custom || row.delivery_address || '',
          deliveryPhone: row.delivery_phone || undefined,
          deliveryDate: row.delivery_date,
          deliveryTime: row.delivery_time || undefined,
          vehiclePlateNumber: row.vehicle_plate_number || undefined,
          vehicleModel: row.vehicle_model || undefined,
          vehicleModelId: row.vehicle_model_id || undefined,
          vehicleDisplayName: row.vehicle_display_name || undefined,
          vehicleType: row.vehicle_type || '',
          vehicleNumber: row.vehicle_number || undefined, // FL-188: VIN
          assignedLandTransporterId: row.assigned_transporter_id || row.transporter_id || undefined,
          transportFee: row.transport_fee || undefined,
          price: row.price || undefined,
          remarks: row.remarks || row.notes || undefined,
          billingClientId: row.billing_client_id || '',
          pickupClientId: row.pickup_client_id || '',
          deliveryClientId: row.delivery_client_id || '',
          pickupReport: row.pickup_report || null,
          deliveryReport: row.delivery_report || null,
          chassisNumber: row.chassis_number || undefined, // FL-163
        })
      );

      logger.info('✅ SupabaseCaseService: getDriverAssignedCases完了', {
        driverId,
        count: cases.length,
        statuses: cases.map(c => c.status).join(', '),
      });

      return cases;
    } catch (error) {
      logger.error('❌ SupabaseCaseService: getDriverAssignedCases実行エラー:', error);
      throw error instanceof DataServiceError
        ? error
        : new DataServiceError(
            'ドライバー割り当て案件の取得に失敗しました',
            ErrorCodes.DATA_FETCH_FAILED,
            error as Error
          );
    }
  }

  // =============================================================================
  // ヘルパーメソッド
  // =============================================================================

  private convertToCaseFormat(row: any): Case {
    return {
      id: row.id,
      status: row.status || 'unassigned',
      billingPartnerId: row.billing_partner_id || undefined,
      pickupPartnerId: row.pickup_partner_id || undefined,
      pickupAddress: row.pickup_address_custom || undefined,
      pickupPhone: row.pickup_phone || undefined,
      pickupDate: row.pickup_date,
      pickupTimeStart: row.pickup_time_start || undefined,
      pickupTimeEnd: row.pickup_time_end || undefined,
      deliveryPartnerId: row.delivery_partner_id || undefined,
      deliveryAddress: row.delivery_address_custom || undefined,
      deliveryPhone: row.delivery_phone || undefined,
      deliveryDate: row.delivery_date,
      deliveryTime: row.delivery_time || undefined,
      vehiclePlateNumber: row.vehicle_plate_number || undefined,
      vehicleModel: row.vehicle_model || undefined,
      vehicleModelId: row.vehicle_model_id || undefined,
      vehicleDisplayName: row.vehicle_display_name || undefined,
      vehicleType: row.vehicle_type || '',
      assignedLandTransporterId: row.assigned_transporter_id || row.transporter_id || undefined,
      transportFee: row.transport_fee || undefined,
      price: row.price || undefined,
      remarks: row.remarks || undefined,
      // Legacy Client IDsの追加
      billingClientId: row.billing_client_id || '',
      pickupClientId: row.pickup_client_id || '',
      deliveryClientId: row.delivery_client_id || '',
      // レポートの追加
      pickupReport: row.pickup_report || null,
      deliveryReport: row.delivery_report || null,

      // FL-163: 受付ステータス管理
      // FL-163: 車体番号 (acceptanceStatus removed - now using status)
      chassisNumber: row.chassis_number || undefined,
    };
  }
}
